import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin } from 'lucide-react';
import { useLang } from '../../context/LangContext';
import { SOCIAL } from '../../config';
import { ROUTES } from '../../_internal';
import LogoFull from '../../assets/logos/LogoFull';
import FooterColumn from '../ui/FooterColumn';

const LINK_CLASS = 'text-text-light hover:text-teal text-sm transition-all duration-200 hover:pl-1';

const FacebookIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
  </svg>
);
const InstagramIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
    <circle cx="12" cy="12" r="4" />
    <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
  </svg>
);
const LinkedInIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
    <rect x="2" y="9" width="4" height="12" />
    <circle cx="4" cy="4" r="2" />
  </svg>
);

const SOCIAL_LINKS = [
  { url: SOCIAL.facebook,  Icon: FacebookIcon,  label: 'Facebook' },
  { url: SOCIAL.instagram, Icon: InstagramIcon, label: 'Instagram' },
  { url: SOCIAL.linkedin,  Icon: LinkedInIcon,  label: 'LinkedIn' },
].filter(s => s.url.trim() !== '');

export default function Footer() {
  const { t } = useLang();

  const anchorLinks = [
    { label: t.nav.home,       href: '#acasa' },
    { label: t.nav.services,   href: '#servicii' },
    { label: t.nav.about,      href: '#despre' },
    { label: t.nav.advantages, href: '#avantaje' },
    { label: t.nav.contact,    href: '#contact' },
    { label: t.nav.locations,  href: '#locatii' },
  ];

  const legalLinks = [
    { label: t.footer.privacy, to: ROUTES.GDPR },
    { label: t.footer.terms,   to: ROUTES.TERMS },
  ];

  const contactItems = [
    { icon: <Phone size={14} />, content: <a href={`tel:${t.company.phone}`}    className="hover:text-teal transition-colors">{t.company.phone}</a> },
    { icon: <Mail size={14} />,  content: <a href={`mailto:${t.company.email}`} className="hover:text-teal transition-colors">{t.company.email}</a> },
    { icon: <MapPin size={14} />, content: <span>{t.company.address}</span> },
  ];

  return (
    <footer className="bg-navy text-silver pt-12 md:pt-20 pb-8">
      <div className="container">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[2fr_1fr_1.5fr] gap-8 md:gap-12 pb-10 md:pb-12 border-b border-white/[0.08]">

          {}
          <div>
            <Link to={ROUTES.HOME} className="inline-block mb-5">
              <LogoFull variant="light" height={40} />
            </Link>
            <p className="text-text-light text-sm leading-relaxed mb-5">{t.company.description}</p>
            <span className="inline-block bg-teal/15 text-teal border border-teal/30 text-[0.72rem] font-bold uppercase tracking-widest px-4 py-1.5 rounded-full">
              {t.footer.gdpBadge}
            </span>

            {}
            {SOCIAL_LINKS.length > 0 && (
              <div className="flex gap-2 mt-5">
                {SOCIAL_LINKS.map(({ url, Icon, label }) => (
                  <a
                    key={label}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    className="w-8 h-8 rounded-full bg-white/[0.06] border border-white/[0.1]
                               flex items-center justify-center text-text-light
                               hover:bg-teal hover:text-navy hover:border-teal
                               transition-all duration-200"
                  >
                    <Icon />
                  </a>
                ))}
              </div>
            )}
          </div>

          {}
          <FooterColumn title={t.footer.navigation}>
            <ul className="flex flex-col gap-2">
              {anchorLinks.map(l => (
                <li key={l.href}>
                  <a href={l.href} className={LINK_CLASS}>{l.label}</a>
                </li>
              ))}
              <li>
                <Link to={ROUTES.CATALOG} className={LINK_CLASS}>{t.nav.catalog}</Link>
              </li>
            </ul>
          </FooterColumn>

          {}
          <FooterColumn title={t.footer.contactTitle}>
            <ul className="flex flex-col gap-3">
              {contactItems.map((item, i) => (
                <li key={i} className="flex items-start gap-2.5 text-text-light text-sm">
                  <span className="text-teal flex-shrink-0 mt-0.5">{item.icon}</span>
                  {item.content}
                </li>
              ))}
            </ul>
            <p className="text-text-light text-xs mt-4 opacity-60">CIF: {t.company.cif}</p>
          </FooterColumn>
        </div>

        {}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pt-6 md:pt-8 text-text-light text-xs">
          <p>© {new Date().getFullYear()} {t.company.name} SRL. {t.footer.copyright}</p>
          <div className="flex flex-wrap gap-4 sm:gap-6">
            {legalLinks.map(link => (
              <Link key={link.to} to={link.to} className="hover:text-teal transition-colors">{link.label}</Link>
            ))}
            <a href="#!" className="hover:text-teal transition-colors">{t.footer.gdpr}</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
